/**
 * Дополнительная запись голоса (§10.1-10.2).
 *
 * Только аудио: видео здесь не нужно, а просить включить камеру ради голоса —
 * лишний барьер. Текст подбирается сервером под недостающее (§10.2).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { voiceDeficitLabels } from '@alterium/shared';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { useRecorder } from '../../features/capture/useRecorder';
import { uploadFile } from '../../features/upload/uploadFile';
import { voiceApi } from '../../api/voice';
import { useTranslation } from 'react-i18next';

const formatTime = (seconds: number): string => {
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export function VoiceImprovePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<number | null>(null);

  // Только звук: камера для голоса не нужна, и просить её — лишний барьер.
  const recorder = useRecorder({ audioOnly: true });
  const script = useQuery({
    queryKey: ['voice-improvement-script'],
    queryFn: voiceApi.improvementScript,
  });

  const targetSec = script.data?.estimatedSec ?? 90;

  useEffect(() => {
    if (recorder.state === 'recording' && recorder.elapsedSec >= targetSec * 2) recorder.stop();
  }, [recorder, recorder.state, recorder.elapsedSec, targetSec]);

  const submit = useMutation({
    mutationFn: async () => {
      const captured = recorder.captured;
      if (!captured) throw new Error(t('consent.noRecording'));

      const extension = captured.mimeType.includes('mp4') ? 'm4a' : 'webm';
      const file = new File([captured.blob], `voice-improve.${extension}`, {
        type: captured.mimeType.split(';')[0] ?? 'audio/webm',
      });

      const asset = await uploadFile(file, {
        kind: 'audio',
        onProgress: ({ loaded, total }) => setProgress(Math.round((loaded / total) * 100)),
      });

      await voiceApi.improve(asset.id, script.data?.version);
    },
    onSuccess: () => {
      recorder.release();
      navigate('/persona', { replace: true });
    },
    onSettled: () => setProgress(null),
  });

  return (
    <div className="flex min-h-full flex-col bg-page">
      <header className="flex items-center justify-between px-5 py-4">
        <span className="text-card-title font-semibold tracking-tight">{t('voice.improve.title')}</span>
        <button
          type="button"
          onClick={() => {
            recorder.release();
            navigate('/persona');
          }}
          aria-label={t('common.close')}
          className="rounded-control p-1.5 text-content-muted transition-colors duration-fast hover:text-content"
        >
          <X className="h-5 w-5" strokeWidth={1.75} />
        </button>
      </header>

      <div className="mx-auto flex w-full max-w-[720px] flex-1 flex-col gap-5 px-5 pb-8">
        {recorder.error ? <Alert tone="error">{recorder.error}</Alert> : null}
        {submit.isError ? <Alert tone="error">{(submit.error as Error).message}</Alert> : null}

        <div>
          <h1 className="text-section-title font-semibold">{t('voice.improve.heading')}</h1>
          <p className="mt-2 text-body text-content-secondary">
            {script.data?.reasons.length
              ? t('voice.improve.reasons', {
                  list: script.data.reasons
                    .map((reason) => voiceDeficitLabels[reason])
                    .join(', '),
                })
              : t('voice.improve.generic')}
          </p>
          <p className="mt-1 text-meta text-content-muted">
            {t('voice.improve.takesAbout', { count: Math.max(1, Math.round(targetSec / 60)) })}
          </p>
        </div>

        <div className="flex flex-col gap-5 rounded-card border border-line bg-surface px-5 py-5">
          {(script.data?.blocks ?? []).map((block, index) => (
            <div key={index}>
              <p className="text-[13px] uppercase tracking-wide text-content-muted">{block.hint}</p>
              <p className="mt-1.5 text-[17px] leading-relaxed text-content">{block.text}</p>
            </div>
          ))}
        </div>

        {recorder.state === 'recording' ? (
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-error" />
            <span className="text-body tabular-nums text-content">
              {formatTime(recorder.elapsedSec)}
            </span>
            <span className="text-meta text-content-muted">/ {formatTime(targetSec)}</span>
          </div>
        ) : null}

        {recorder.state === 'recorded' && recorder.captured ? (
          <audio src={recorder.captured.url} controls className="w-full max-w-[420px]" />
        ) : null}

        {progress !== null ? (
          <div className="progress-track">
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>
        ) : null}

        <div className="flex flex-wrap gap-3">
          {recorder.state === 'idle' || recorder.state === 'error' ? (
            <Button variant="primary" onClick={() => void recorder.requestPermission()}>
              {recorder.state === 'error' ? t('common.tryAgain') : t('voice.improve.enableMic')}
            </Button>
          ) : null}

          {recorder.state === 'ready' ? (
            <Button variant="primary" onClick={() => void recorder.start()}>{t('capture.start')}</Button>
          ) : null}

          {recorder.state === 'recording' ? (
            <Button onClick={recorder.stop} disabled={recorder.elapsedSec < 20}>{t('common.done')}</Button>
          ) : null}

          {recorder.state === 'recorded' ? (
            <>
              <Button variant="primary" onClick={() => submit.mutate()} disabled={submit.isPending}>
                {submit.isPending ? t('auth.sending') : t('common.send')}
              </Button>
              <Button onClick={recorder.reset} disabled={submit.isPending}>{t('capture.rerecord')}</Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
